import { Controller } from "@hotwired/stimulus"

// Dispatches `ios-upload:start` / `ios-upload:end` on the document so the
// panel controller can lock/unlock other channels' actions during an upload.
export default class extends Controller {
  static targets = [
    "form",
    "input",
    "idle",
    "progress",
    "bar",
    "filename",
    "cancel",
  ]
  static values = { category: String }

  connect() {
    this.boundDragOver = this.onDragOver.bind(this)
    this.boundDragLeave = this.onDragLeave.bind(this)
    this.boundDrop = this.onDrop.bind(this)
    this.element.addEventListener("dragover", this.boundDragOver)
    this.element.addEventListener("dragleave", this.boundDragLeave)
    this.element.addEventListener("drop", this.boundDrop)

    this.boundStart = this.onUploadStart.bind(this)
    this.boundProgress = this.onUploadProgress.bind(this)
    this.boundEnd = this.onUploadEnd.bind(this)
    this.boundError = this.onUploadError.bind(this)
    this.boundBeforeStorage = this.onBeforeStorageRequest.bind(this)
    this.inputTarget.addEventListener("direct-upload:start", this.boundStart)
    this.inputTarget.addEventListener(
      "direct-upload:progress",
      this.boundProgress,
    )
    this.inputTarget.addEventListener("direct-upload:end", this.boundEnd)
    this.inputTarget.addEventListener("direct-upload:error", this.boundError)
    this.inputTarget.addEventListener(
      "direct-upload:before-storage-request",
      this.boundBeforeStorage,
    )
  }

  disconnect() {
    this.element.removeEventListener("dragover", this.boundDragOver)
    this.element.removeEventListener("dragleave", this.boundDragLeave)
    this.element.removeEventListener("drop", this.boundDrop)
    this.inputTarget.removeEventListener("direct-upload:start", this.boundStart)
    this.inputTarget.removeEventListener(
      "direct-upload:progress",
      this.boundProgress,
    )
    this.inputTarget.removeEventListener("direct-upload:end", this.boundEnd)
    this.inputTarget.removeEventListener("direct-upload:error", this.boundError)
    this.inputTarget.removeEventListener(
      "direct-upload:before-storage-request",
      this.boundBeforeStorage,
    )
  }

  pickFile(event) {
    if (event.target.closest("button, a, input")) return
    this.inputTarget.click()
  }

  onDragOver(event) {
    event.preventDefault()
    if (this.isUploading) return
    this.element.classList.add("is-dragover")
  }

  onDragLeave(event) {
    if (event.target !== this.element) return
    this.element.classList.remove("is-dragover")
  }

  onDrop(event) {
    event.preventDefault()
    this.element.classList.remove("is-dragover")
    if (this.isUploading) return
    if (!event.dataTransfer.files.length) return

    this.inputTarget.files = event.dataTransfer.files
    this.submit()
  }

  fileChosen() {
    if (this.inputTarget.files.length === 0) return
    this.submit()
  }

  submit() {
    const file = this.inputTarget.files[0]
    if (this.hasFilenameTarget)
      this.filenameTarget.textContent = file?.name || ""
    this.showProgress(0)
    this.formTarget.requestSubmit()
  }

  cancel(event) {
    event.preventDefault()
    if (!this.isUploading) return

    if (this.storageXhr) {
      this.storageXhr.abort()
    }
    this.reset("canceled")
  }

  onBeforeStorageRequest(event) {
    this.storageXhr = event.detail.xhr
  }

  onUploadStart() {
    this.isUploading = true
    this.idleTarget.classList.add("hidden")
    this.progressTarget.classList.remove("hidden")
    this.dispatchUpload("start")
  }

  onUploadProgress(event) {
    const pct = Math.max(0, Math.min(100, event.detail.progress || 0))
    this.showProgress(pct)
  }

  onUploadEnd() {
    this.showProgress(100)
    this.storageXhr = null
    // The Turbo-driven page reload that follows the form POST tears down the
    // UI; we don't reset() here or the bar would snap to 0 mid-flight.
  }

  onUploadError(event) {
    event.preventDefault()
    this.reset("error")
  }

  showProgress(pct) {
    this.barTarget.style.width = `${pct}%`
  }

  reset(status) {
    this.isUploading = false
    this.storageXhr = null
    this.inputTarget.value = ""
    this.progressTarget.classList.add("hidden")
    this.idleTarget.classList.remove("hidden")
    this.showProgress(0)
    this.dispatchUpload("end", { status })
  }

  dispatchUpload(phase, detail = {}) {
    document.dispatchEvent(
      new CustomEvent(`ios-upload:${phase}`, {
        bubbles: true,
        detail: { category: this.categoryValue, ...detail },
      }),
    )
  }
}
